import React, { useEffect, useState } from "react";
import {
  Table, Tag, Input, message, Button, Dropdown, Space,
  Tooltip, Drawer, Divider, Modal, DatePicker, Pagination
} from "antd";
import {
  SearchOutlined, MessageOutlined, EyeOutlined, DeleteOutlined,
  ReloadOutlined, FileExcelOutlined, FilePdfOutlined, DownOutlined, UpOutlined,
  ClockCircleOutlined, CheckCircleOutlined, SyncOutlined, StopOutlined,
  MailOutlined, PhoneOutlined, CalendarOutlined, IdcardOutlined,
  UserOutlined, FileTextOutlined, WhatsAppOutlined, CopyOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import {
  User_ContactGet, User_ContactRemove, User_ContactUpdate,
} from "../../../../Api/CoreApi";
import { exportToCSV } from "../../Utils/exportCSV";
import { exportToPDF } from "../../Utils/exportPDF";
import {
  formatDate, formatDateTime, generateRefNumber, capitalize,
} from "../../Utils/formatters";
import "./ContactEnquiries.css";

const { RangePicker } = DatePicker;

function ContactEnquiries() {
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRange, setDateRange] = useState(null);

  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // 📱 Mobile Responsiveness States
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileStats, setShowMobileStats] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ============================================
  // FETCH DATA
  // ============================================
  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await User_ContactGet();
      const arr = Array.isArray(response) ? response : [];
      const sorted = arr.sort(
        (a, b) => new Date(b.created_date || 0) - new Date(a.created_date || 0)
      );
      setContacts(sorted);
      setFilteredContacts(sorted);
    } catch {
      message.error("Failed to fetch contacts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchContacts(); }, []);

  // ============================================
  // FILTER LOGIC
  // ============================================
  useEffect(() => {
    let filtered = [...contacts];

    if (searchText) {
      filtered = filtered.filter(
        (c) =>
          c.name?.toLowerCase().includes(searchText.toLowerCase()) ||
          c.full_name?.toLowerCase().includes(searchText.toLowerCase()) ||
          c.email?.toLowerCase().includes(searchText.toLowerCase()) ||
          c.mobile?.includes(searchText) ||
          c.contact?.includes(searchText) ||
          c.subject?.toLowerCase().includes(searchText.toLowerCase()) ||
          generateRefNumber("contact", c.id).toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((c) => (c.status || "new") === statusFilter);
    }

    if (dateRange && dateRange.length === 2) {
      filtered = filtered.filter((c) => {
        const date = new Date(c.created_date || c.date);
        return date >= dateRange[0].toDate() && date <= dateRange[1].toDate();
      });
    }

    setFilteredContacts(filtered);
    setCurrentPage(1); // Reset page on filter
  }, [searchText, statusFilter, dateRange, contacts]);

  // ============================================
  // HELPERS
  // ============================================
  const getStatusConfig = (status) => {
    const config = {
      new: { color: "new", icon: <ClockCircleOutlined />, label: "New" },
      inprogress: { color: "inprogress", icon: <SyncOutlined spin />, label: "In Progress" },
      resolved: { color: "resolved", icon: <CheckCircleOutlined />, label: "Resolved" },
      closed: { color: "closed", icon: <StopOutlined />, label: "Closed" },
    };
    return config[status || "new"] || config.new;
  };

  // ============================================
  // ACTIONS
  // ============================================
  const handleView = (record) => {
    setSelectedContact(record);
    setDetailsDrawerOpen(true);
  };

  const handleCall = (mobile) => {
    if (!mobile) return message.warning("No contact number available");
    window.location.href = `tel:${mobile}`;
  };

  const handleWhatsApp = (mobile) => {
    if (!mobile) return message.warning("No contact number available");
    window.open(`https://wa.me/${mobile.replace(/\D/g, "")}`, "_blank");
  };

  const handleEmail = (email) => {
    if (!email) return message.warning("No email available");
    window.location.href = `mailto:${email}`;
  };

  const handleCopy = (text, label = "Text") => {
    navigator.clipboard.writeText(text);
    message.success(`${label} copied to clipboard`);
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await User_ContactUpdate(id, { status });
      message.success(`Marked as ${capitalize(status)}`);
      fetchContacts();
      if (selectedContact?.id === id) {
        setSelectedContact({ ...selectedContact, status });
      }
    } catch {
      message.error("Failed to update status");
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete Enquiry?",
      content: "This enquiry will be permanently deleted. This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await User_ContactRemove(id);
          message.success("Enquiry deleted successfully");
          fetchContacts();
          setDetailsDrawerOpen(false);
        } catch {
          message.error("Failed to delete");
        }
      },
    });
  };

  const clearFilters = () => {
    setSearchText("");
    setStatusFilter("all");
    setDateRange(null);
  };

  // ============================================
  // EXPORT
  // ============================================
  const exportColumns = [
    { key: "sno", label: "S.No." },
    { key: "contact_ref", label: "Contact Ref", format: (r) => generateRefNumber("contact", r.id || 0) },
    { key: "name", label: "Name", format: (r) => r.name || r.full_name || "N/A" },
    { key: "email", label: "Email", format: (r) => r.email || "N/A" },
    { key: "mobile", label: "Mobile", format: (r) => r.mobile || r.contact || r.phone || "N/A" },
    { key: "subject", label: "Subject", format: (r) => r.subject || "N/A" },
    { key: "message", label: "Message", format: (r) => (r.message || "N/A").slice(0, 100) },
    { key: "status", label: "Status", format: (r) => capitalize(r.status || "new") },
    { key: "date", label: "Submitted Date", format: (r) => formatDateTime(r.created_date || r.date) },
  ];

  const handleExportCSV = (all = false) => {
    const data = all ? contacts : filteredContacts;
    exportToCSV(data, exportColumns, "contact_enquiries_report");
  };

  const handleExportPDF = (all = false) => {
    const data = all ? contacts : filteredContacts;
    exportToPDF(data, exportColumns, {
      title: "Contact Enquiries Report",
      subtitle: "Complete list of contact enquiries and support messages",
      filename: "contact_enquiries_report",
    });
  };

  // ============================================
  // STATS
  // ============================================
  const stats = {
    total: contacts.length,
    new: contacts.filter(c => (c.status || "new") === "new").length,
    inprogress: contacts.filter(c => c.status === "inprogress").length,
    resolved: contacts.filter(c => c.status === "resolved").length,
    closed: contacts.filter(c => c.status === "closed").length,
    today: contacts.filter(c => {
      const d = new Date(c.created_date);
      const today = new Date();
      return d.toDateString() === today.toDateString();
    }).length,
  };

  // ============================================
  // COLUMNS
  // ============================================
  const columns = [
    {
      title: "#", key: "sno", width: 45, fixed: "left",
      render: (_, __, i) => (
        <span className="sno-cell">
          {(currentPage - 1) * pageSize + i + 1}
        </span>
      ),
    },
    {
      title: "Ref", key: "contact_ref", width: 100,
      render: (_, r) => <span className="ref-cell">{generateRefNumber("contact", r.id)}</span>,
    },
    {
      title: "Sender", key: "sender", width: 200,
      render: (_, r) => (
        <div className="sender-cell">
          <div className="sender-avatar">
            {(r.name?.[0] || r.full_name?.[0] || "U").toUpperCase()}
          </div>
          <div className="sender-info">
            <div className="sender-name">{r.name || r.full_name || "N/A"}</div>
            <div className="sender-email">{r.email || "No email"}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Mobile / Call", key: "mobile", width: 170,
      render: (_, r) => {
        const mobile = r.mobile || r.contact || r.phone;
        return (
          <div className="mobile-call-cell">
            <span className="mobile-number">
              <PhoneOutlined /> {mobile || "N/A"}
            </span>
            {mobile && (
              <Tooltip title={`Call ${mobile}`}>
                <Button
                  size="small"
                  icon={<PhoneOutlined />}
                  className="call-quick-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCall(mobile);
                  }}
                >
                  Call
                </Button>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Subject", dataIndex: "subject", key: "subject", width: 200,
      render: (subject, r) => (
        <div className="subject-cell">
          <div className="subject-title">
            {subject || "General Enquiry"}
          </div>
          {r.message && (
            <div className="subject-preview">
              {r.message.slice(0, 40)}...
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Status", dataIndex: "status", key: "status", width: 120,
      render: (status) => {
        const cfg = getStatusConfig(status);
        return (
          <Tag className={`status-tag ${cfg.color}`}>
            {cfg.icon} {cfg.label}
          </Tag>
        );
      },
    },
    {
      title: "Submitted", key: "date", width: 130,
      render: (_, r) => (
        <div className="date-cell">
          <div className="date-main">{formatDate(r.created_date || r.date)}</div>
          <div className="date-time">
            {r.created_date ? new Date(r.created_date).toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
            }) : ""}
          </div>
        </div>
      ),
    },
    {
      title: "Actions", key: "action", width: 150, fixed: "right",
      render: (_, record) => (
        <Space size={3}>
          <Tooltip title="View Details">
            <Button size="small" icon={<EyeOutlined />} className="action-btn view"
              onClick={() => handleView(record)} />
          </Tooltip>
          <Tooltip title="Send Email">
            <Button size="small" icon={<MailOutlined />} className="action-btn email"
              onClick={() => handleEmail(record.email)} />
          </Tooltip>
          <Tooltip title="WhatsApp">
            <Button size="small" icon={<WhatsAppOutlined />} className="action-btn whatsapp"
              onClick={() => handleWhatsApp(record.mobile || record.contact)} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button size="small" icon={<DeleteOutlined />} className="action-btn delete"
              onClick={() => handleDelete(record.id)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const csvMenuItems = {
    items: [
      { key: "1", label: `Export Filtered (${filteredContacts.length})`, onClick: () => handleExportCSV(false) },
      { key: "2", label: `Export All (${contacts.length})`, onClick: () => handleExportCSV(true) },
    ],
  };

  const pdfMenuItems = {
    items: [
      { key: "1", label: `Export Filtered (${filteredContacts.length})`, onClick: () => handleExportPDF(false) },
      { key: "2", label: `Export All (${contacts.length})`, onClick: () => handleExportPDF(true) },
    ],
  };

  return (
    <div className="contact-list-page">

      {/* ============ HEADER + STATS ============ */}
      <div className="header-stats-wrapper">
        <div className="page-header">
          {/* LEFT SIDE */}
          <div className="header-left">
            <div className="title-row">
              <div className="title-icon"><MessageOutlined /></div>
              <div className="title-content">
                <h2 className="page-title">Contact Enquiries</h2>
                <p className="page-subtitle">
                  {filteredContacts.length} of {contacts.length} enquiries
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT SIDE: Buttons */}
          <div className="header-actions">
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined />} onClick={fetchContacts} loading={loading} className="icon-btn" />
            </Tooltip>
            <Dropdown menu={csvMenuItems}>
              <Button icon={<FileExcelOutlined />} className="csv-btn">
                <span>CSV</span>
              </Button>
            </Dropdown>
            <Dropdown menu={pdfMenuItems}>
              <Button icon={<FilePdfOutlined />} className="pdf-btn">
                <span>PDF</span>
              </Button>
            </Dropdown>

            {/* 📱 Mobile Stats Toggle (Arrow) */}
            {isMobile && (
              <button
                type="button"
                className="mobile-stats-icon-btn"
                onClick={() => setShowMobileStats(prev => !prev)}
              >
                {showMobileStats ? <UpOutlined /> : <DownOutlined />}
              </button>
            )}
          </div>
        </div>

        {/* Animated Smooth Stats Accordion */}
        <div className={`mini-stats-bar ${isMobile && showMobileStats ? 'show' : ''}`}>
          <div className="mini-stat total">
            <MessageOutlined />
            <div className="stat-content">
              <b>{stats.total}</b> <span>Total</span>
            </div>
          </div>
          <div className="mini-divider" />

          <div className="mini-stat new">
            <ClockCircleOutlined />
            <div className="stat-content">
              <b>{stats.new}</b> <span>New</span>
            </div>
          </div>
          <div className="mini-divider" />

          <div className="mini-stat inprogress">
            <SyncOutlined />
            <div className="stat-content">
              <b>{stats.inprogress}</b> <span>In Progress</span>
            </div>
          </div>
          <div className="mini-divider" />

          <div className="mini-stat resolved">
            <CheckCircleOutlined />
            <div className="stat-content">
              <b>{stats.resolved}</b> <span>Resolved</span>
            </div>
          </div>
          <div className="mini-divider" />

          <div className="mini-stat closed">
            <StopOutlined />
            <div className="stat-content">
              <b>{stats.closed}</b> <span>Closed</span>
            </div>
          </div>
          <div className="mini-divider" />

          <div className="mini-stat today">
            <CalendarOutlined />
            <div className="stat-content">
              <b>{stats.today}</b> <span>Today</span>
            </div>
          </div>
        </div>
      </div>

      {/* ============ QUICK FILTER TABS ============ */}
      <div className="quick-filters-bar">
        <div className="filter-tabs">
          <button className={`filter-tab ${statusFilter === "all" ? "active" : ""}`} onClick={() => setStatusFilter("all")}>
            <MessageOutlined /> All <span className="tab-count">{stats.total}</span>
          </button>
          <button className={`filter-tab new ${statusFilter === "new" ? "active" : ""}`} onClick={() => setStatusFilter("new")}>
            <ClockCircleOutlined /> New <span className="tab-count">{stats.new}</span>
          </button>
          <button className={`filter-tab inprogress ${statusFilter === "inprogress" ? "active" : ""}`} onClick={() => setStatusFilter("inprogress")}>
            <SyncOutlined /> In Progress <span className="tab-count">{stats.inprogress}</span>
          </button>
          <button className={`filter-tab resolved ${statusFilter === "resolved" ? "active" : ""}`} onClick={() => setStatusFilter("resolved")}>
            <CheckCircleOutlined /> Resolved <span className="tab-count">{stats.resolved}</span>
          </button>
          <button className={`filter-tab closed ${statusFilter === "closed" ? "active" : ""}`} onClick={() => setStatusFilter("closed")}>
            <StopOutlined /> Closed <span className="tab-count">{stats.closed}</span>
          </button>
        </div>
      </div>

      {/* ============ SEARCH & DATE FILTER ============ */}
      <div className="filters-card">
        <div className="filters-row">

          <div className="filter-group filter-search">
            <label className="filter-label">🔍 Search</label>
            <Input
              placeholder="Search by name, mobile, email..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="premium-search-box"
              allowClear
            />
          </div>
          <div className="filters-display">
            <div className="filter-group filter-date">
              <label className="filter-label">📅 Date Range</label>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                className="premium-date-picker"
                placeholder={['Start date', 'End date']}
              />
            </div>


            <div className="filter-group filter-clear">
              <label className="filter-label desktop-only-label" style={{ visibility: 'hidden' }}>Clear</label>
              <Button className="clear-filters-btn" onClick={clearFilters} icon={<ReloadOutlined />}>
                Clear All
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ============ TABLE / MOBILE CARDS ============ */}
      <div className="table-card">
        {isMobile ? (
          <div className="mobile-user-list">
            {filteredContacts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 20, color: '#999' }}>No enquiries found</div>
            ) : (
              filteredContacts.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((r) => {
                const statusCfg = getStatusConfig(r.status);
                const mobile = r.mobile || r.contact || r.phone;
                return (
                  <div className="mobile-user-card" key={r.id}>
                    <div className="muc-header">
                      <span className="muc-id">{generateRefNumber("contact", r.id)}</span>
                      <span className={`muc-status ${statusCfg.color}`}>
                        {statusCfg.icon} {statusCfg.label}
                      </span>
                    </div>

                    <div className="muc-body">
                      <div className="muc-avatar" style={{ background: 'linear-gradient(135deg, #1890ff, #40a9ff)' }}>
                        {(r.name?.[0] || r.full_name?.[0] || "U").toUpperCase()}
                      </div>
                      <div className="muc-info">
                        <h4 className="muc-name">{r.name || r.full_name || "N/A"}</h4>
                        <span className="muc-phone"><PhoneOutlined /> {mobile || "N/A"}</span>
                      </div>
                    </div>

                    <div style={{ padding: '0 12px 10px', fontSize: 12, color: '#334155' }}>
                      <strong>Sub:</strong> {r.subject || "General Enquiry"}
                    </div>

                    <div className="muc-footer">
                      <div className="muc-plan" style={{ color: '#888' }}>
                        {formatDate(r.created_date || r.date)}
                      </div>
                      <div className="muc-actions">
                        <button className="action-btn view" onClick={() => handleView(r)}><EyeOutlined /></button>
                        <button className="action-btn whatsapp" onClick={() => handleWhatsApp(mobile)}><WhatsAppOutlined /></button>
                        <button className="action-btn delete" onClick={() => handleDelete(r.id)}><DeleteOutlined /></button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}

            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={filteredContacts.length}
              onChange={(page, size) => { setCurrentPage(page); setPageSize(size); }}
              size="small"
              style={{ textAlign: 'center', marginTop: 16 }}
            />
          </div>
        ) : (
          <div className="desktop-table-wrapper">
            <Table
              rowKey="id"
              columns={columns}
              dataSource={filteredContacts}
              loading={loading}
              size="small"
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                showSizeChanger: true,
                size: "small",
                showTotal: (total) => `Total ${total}`,
                onChange: (page, size) => {
                  setCurrentPage(page);
                  setPageSize(size);
                },
              }}
              scroll={{ x: 1100 }}
            />
          </div>
        )}
      </div>

      {/* ============ DETAILS DRAWER ============ */}
      <Drawer
        title={<div className="drawer-title"><MessageOutlined /> Enquiry Details</div>}
        placement="right"
        width={500}
        onClose={() => setDetailsDrawerOpen(false)}
        open={detailsDrawerOpen}
      >
        {selectedContact && (
          <div className="enquiry-details">

            {/* Header */}
            <div className="enquiry-header">
              <div className="enquiry-avatar">
                {(selectedContact.name?.[0] || selectedContact.full_name?.[0] || "U").toUpperCase()}
              </div>
              <h2>{selectedContact.name || selectedContact.full_name || "N/A"}</h2>
              <p className="enquiry-ref">
                {generateRefNumber("contact", selectedContact.id)}
              </p>
              <div className="enquiry-tags">
                <Tag className={`status-tag ${getStatusConfig(selectedContact.status).color}`}>
                  {getStatusConfig(selectedContact.status).icon}
                  {" "}{getStatusConfig(selectedContact.status).label}
                </Tag>
              </div>

              {/* Quick Actions */}
              <div className="quick-actions-row">
                {(selectedContact.mobile || selectedContact.contact) && (
                  <>
                    <Button
                      icon={<PhoneOutlined />}
                      className="quick-action-btn call"
                      onClick={() => handleCall(selectedContact.mobile || selectedContact.contact)}
                    >
                      Call
                    </Button>
                    <Button
                      icon={<WhatsAppOutlined />}
                      className="quick-action-btn whatsapp"
                      onClick={() => handleWhatsApp(selectedContact.mobile || selectedContact.contact)}
                    >
                      WhatsApp
                    </Button>
                  </>
                )}
                {selectedContact.email && (
                  <Button
                    icon={<MailOutlined />}
                    className="quick-action-btn email"
                    onClick={() => handleEmail(selectedContact.email)}
                  >
                    Email
                  </Button>
                )}
              </div>
            </div>

            <Divider style={{ margin: "12px 0" }} />

            {/* Contact Info */}
            <div className="detail-section">
              <h4><UserOutlined /> Contact Information</h4>
              <div className="detail-item">
                <UserOutlined /><strong>Name</strong>
                <span>{selectedContact.name || selectedContact.full_name || "N/A"}</span>
              </div>
              <div className="detail-item">
                <MailOutlined /><strong>Email</strong>
                <span className="copyable" onClick={() => handleCopy(selectedContact.email, "Email")}>
                  {selectedContact.email || "N/A"}
                  {selectedContact.email && <CopyOutlined className="copy-icon" />}
                </span>
              </div>
              <div className="detail-item">
                <PhoneOutlined /><strong>Mobile</strong>
                <span className="copyable" onClick={() => handleCopy(selectedContact.mobile || selectedContact.contact, "Mobile")}>
                  {selectedContact.mobile || selectedContact.contact || "N/A"}
                  {(selectedContact.mobile || selectedContact.contact) && <CopyOutlined className="copy-icon" />}
                </span>
              </div>
            </div>

            <Divider style={{ margin: "12px 0" }} />

            {/* Enquiry Details */}
            <div className="detail-section">
              <h4><QuestionCircleOutlined /> Enquiry Details</h4>
              <div className="detail-item">
                <IdcardOutlined /><strong>Ref No</strong>
                <span className="ref-highlight">
                  {generateRefNumber("contact", selectedContact.id)}
                </span>
              </div>
              <div className="detail-item">
                <FileTextOutlined /><strong>Subject</strong>
                <span className="subject-highlight">
                  {selectedContact.subject || "General Enquiry"}
                </span>
              </div>
              <div className="detail-item">
                <CalendarOutlined /><strong>Submitted</strong>
                <span>{formatDateTime(selectedContact.created_date || selectedContact.date)}</span>
              </div>
            </div>

            <Divider style={{ margin: "12px 0" }} />

            {/* Message */}
            <div className="detail-section">
              <h4><MessageOutlined /> Message</h4>
              <div className="enquiry-message">
                {selectedContact.message || "No message provided"}
              </div>
            </div>

            <Divider style={{ margin: "12px 0" }} />

            {/* Status Actions - All 4 statuses */}
            <div className="detail-section">
              <h4><SyncOutlined /> Update Status</h4>
              <div className="status-actions-grid">
                <Button
                  icon={<ClockCircleOutlined />}
                  className={`status-btn new ${(selectedContact.status || "new") === "new" ? "active-status" : ""}`}
                  onClick={() => handleStatusUpdate(selectedContact.id, "new")}
                >
                  New
                </Button>
                <Button
                  icon={<SyncOutlined />}
                  className={`status-btn inprogress ${selectedContact.status === "inprogress" ? "active-status" : ""}`}
                  onClick={() => handleStatusUpdate(selectedContact.id, "inprogress")}
                >
                  In Progress
                </Button>
                <Button
                  icon={<CheckCircleOutlined />}
                  className={`status-btn resolved ${selectedContact.status === "resolved" ? "active-status" : ""}`}
                  onClick={() => handleStatusUpdate(selectedContact.id, "resolved")}
                >
                  Resolved
                </Button>
                <Button
                  icon={<StopOutlined />}
                  className={`status-btn closed ${selectedContact.status === "closed" ? "active-status" : ""}`}
                  onClick={() => handleStatusUpdate(selectedContact.id, "closed")}
                >
                  Closed
                </Button>
              </div>
            </div>

            <Divider style={{ margin: "12px 0" }} />

            {/* Delete Action */}
            <div className="drawer-danger-zone">
              <Button
                danger
                icon={<DeleteOutlined />}
                block
                onClick={() => handleDelete(selectedContact.id)}
              >
                Delete This Enquiry
              </Button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}

export default ContactEnquiries;